<template>
  <div class="your-responsibility-checklist-tab">
    <div v-if="showBreadcrumb" class="breadcrumb-container">
      <ExBreadcrumbs
        :breadcrumbsList="breadcrumbItems"
        @onClickBreadcrumb="handleBreadcrumbClick"
      />
    </div>
    <AsEmployeeResponsibilityAssignmentHeader
      :responsibility="responsibility"
      :responsibilityName="responsibilityName"
      :roleName="roleName"
      :positionName="positionName"
      :assignedOn="assignedOn"
      :availableInspectors="availableInspectors"
      :inspectionStatus="inspectionStatus"
      :checkpointCounts="checkpointCounts"
      :currentBadgeStatus="currentBadgeStatus"
      :readinessLevel="readinessLevel"
      :readinessDot="readinessDot"
      class="assignment-header"
    />
    <AsEmployeeInspectionBanner
      v-if="showInspectionRequest"
      :state="inspectionBannerState"
      :inspector="inspectionBannerInspectorAccepted"
      :expedited="expedited === true"
      :readinessLevel="readinessLevel"
      @onRequest="handleInspectionRequest"
      @onCancel="handleInspectionCancel"
      @onChatter="handleInspectionChatter"
      class="inspection-banner-wrap"
    />

    <EmployeeHorizontalMenu
      :menuItems="menuItems"
      :initialTab="selectedTab"
      @tab-selected="handleTabSelected"
    />

    <template v-if="selectedTab === 'Checklist'">
      <AsEmployeeChecklistInspectionCard
        v-if="ideaMDP452 && checklistLoaded"
        class="checklist-inspection"
        :levels="checklistLevels"
        :pass-filter="checklistPassFilter"
        :search-query="checklistSearchQuery"
        @update:passFilter="checklistPassFilter = $event"
        @update:searchQuery="checklistSearchQuery = $event"
        @toggle-level="handleChecklistToggleLevel"
        @action-click="handleChecklistActionClick"
        @secondary-action-click="handleChecklistSecondaryActionClick"
      />

      <YourResponsibilityChecklistTable
        v-else
        :ideaMDP452="ideaMDP452"
        :loading="checklistLoading"
        :data="checkpointsData"
        :pagination="pagination"
        :levelCounter="levelCounter"
        @appliedFilter="handleAppliedFilter"
        @filtersChanged="handleFiltersChanged"
        @paginationChanged="handlePaginationChanged"
        @mdp-cta="emit('mdp-cta', $event)"
        @complete-microlearning-click="emit('complete-microlearning-click', $event)"
        @request-more-checkpoints-chatter="emit('request-more-checkpoints-chatter', $event)"
      />
    </template>

    <div v-else-if="selectedTab === 'Available Inspectors'" class="">
      <ResponsibilityAvailableInspectorsCards
        :availableInspectors="availableInspectors"
        :showInspectorsHeader="true"
      />
    </div>

    <div v-else-if="selectedTab === 'Purpose'" class="responsibility-purpose-tab">
      <PurposeViewEdit :value="responsibilityPurpose" :readOnly="true" />
    </div>

    <RequestInspectionDialog
      :value="showRequestInspectionDialog"
      :responsibilityName="responsibilityName"
      :inspectors="availableInspectors"
      :has-inspectors="hasRequestDialogInspectors"
      @input="showRequestInspectionDialog = $event"
      @onRequest="handleDialogMakeRequest"
      @onClose="showRequestInspectionDialog = false"
    />

    <CancelInspectionRequestDialog
      :value="showCancelInspectionDialog"
      :responsibilityName="responsibilityName"
      actionLabel="Cancel request"
      @input="showCancelInspectionDialog = $event"
      @onClose="showCancelInspectionDialog = false"
      @onAction="handleCancelInspectionConfirm"
    />

    <v-navigation-drawer
      v-model="showCheckpointDrawer"
      right
      app
      temporary
      :width="drawerWidth"
      class="checkpoint-drawer"
    >
      <AsEmployeeCheckpointDrawer
        v-if="drawerCheckpointItem"
        :employee-name="drawerEmployee.employeeName"
        :first-name="drawerEmployee.firstName"
        :last-name="drawerEmployee.lastName"
        :job-title="drawerEmployee.jobTitle"
        :avatar="drawerEmployee.avatar"
        :level="drawerLevel"
        :criteria="drawerCriteria"
        :responsibility-name="responsibilityName"
        :responsibility-code="drawerResponsibilityCode"
        :sections="drawerSections"
        :current-step="drawerCurrentStep"
        :checkpoint-id="resolveDrawerCheckpointId()"
        :responsibility-id="responsibilityId"
        :quiz-attempt-result="drawerQuizAttemptResult"
        :readonly="drawerReadonly"
        :employee-id="resolveEmployeeId()"
        :learning-events="drawerLearningEvents"
        :visible="showCheckpointDrawer"
        :inspection-status="drawerInspectionStatus"
        :inspection-description="drawerInspectionDescription"
        :inspector-name="drawerInspectorName"
        :inspection-date="drawerInspectionDate"
        :inspectors="availableInspectors"
        @update:current-step="drawerCurrentStep = $event"
        @next="handleDrawerNext"
        @quiz-submit="handleDrawerQuizSubmit"
        @learning-event-recorded="reloadChecklistInBackground"
        @close="closeCheckpointDrawer"
      />
    </v-navigation-drawer>

    <ChatterDrawer
      v-if="showInspectionChatter"
      :drawer="showInspectionChatter"
      type="Inspection Request"
      :typeValue="responsibilityName"
      :listData="inspectionChatterRecord"
      object-type="evaluation_request"
      :object-id="inspectionChatterRecord.id"
      :chatterList="inspectionChatterList"
      :totalCheckpointChatter="inspectionChatterTotal"
      iconName="org_icon_responsibility"
      @closeDrawer="closeInspectionChatter"
      @requestMoreCheckpointsChatter="loadMoreInspectionChatter"
    />
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, watch, onMounted } from "vue";
import {
  requestInspection,
  cancelInspectionRequest,
  getEmployeeEvaluation,
  getEvaluationSummaryByLevel,
  getEmployeeChecklist,
  getEmployeeCheckpointDetail,
  getCheckpointVideoSignedUrl,
  postCheckpointInspectionEvent,
  submitCheckpointQuizAttempt,
  getChatterList,
} from "@/api";
import {
  buildChecklistInspectionLevels,
  buildCheckpointTitle,
  resolveLevelName,
  resolveChecklistQueryParams,
  EMPLOYEE_CHECKLIST_LEVELS,
} from "@components/utils/buildChecklistInspectionLevels";
import {
  isApiVideoPlaybackUrl,
  needsSignedVideoUrlResolution,
} from "@/utils/activeCheckpointMicrolearningValidity";
import { isCloudflareSignedUrl } from "@/utils/simulatedCloudflareStreamWebhook";
import {
  buildCheckpointDrawerSections,
  buildInspectorOnlyDrawerSections,
  buildSubmittedQuizAttempt,
  extractInspectionVerdictFromChatter,
  extractPlaybackUrlFromMicrolearningInstructions,
  findResumeStep,
  isInspectorOnlyCheckpoint,
  resolveInspectionDrawerStatus,
} from "@components/utils/buildCheckpointDrawerSections";
import { LD_FLAG_IDEA_MDP_625 } from "@components/constants/launchDarklyFlagKeys";
import AsEmployeeCheckpointDrawer from "@components/AsEmployeeCheckpointDrawer/AsEmployeeCheckpointDrawer.vue";
import AsEmployeeResponsibilityAssignmentHeader from "@components/AsEmployeeResponsibilityAssignmentHeader/AsEmployeeResponsibilityAssignmentHeader.vue";
import AsEmployeeInspectionBanner from "@components/AsEmployeeInspectionBanner/AsEmployeeInspectionBanner.vue";
import YourResponsibilityChecklistTable from "@components/YourResponsibilityChecklistTable/YourResponsibilityChecklistTable.vue";
import AsEmployeeChecklistInspectionCard from "@components/AsEmployeeChecklistInspectionCard/AsEmployeeChecklistInspectionCard.vue";
import EmployeeHorizontalMenu from "@components/EmployeeHorizontalMenu/EmployeeHorizontalMenu.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import ResponsibilityAvailableInspectorsCards from "@components/ResponsibilityAvailableInspectorsCards/ResponsibilityAvailableInspectorsCards.vue";
import PurposeViewEdit from "@components/PurposeViewEdit/PurposeViewEdit.vue";
import RequestInspectionDialog from "@components/RequestInspectionDialog/RequestInspectionDialog.vue";
import CancelInspectionRequestDialog from "@components/CancelInspectionRequestDialog/CancelInspectionRequestDialog.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import { transformChatterData } from "@/utils/chatterUtils";
// Props
const props = defineProps({
  responsibility: {
    type: Object,
    default: () => ({
      responsibilityCode: "",
      responsibilityImportance: "",
      roleCode: "",
      roleImportance: "",
      positionCode: "",
      positionImportance: "",
      positionCommitment: "",
      newDuration: "",
      show_codes: false,
    }),
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  roleName: {
    type: String,
    default: "",
  },
  positionName: {
    type: String,
    default: "",
  },
  assignedOn: {
    type: String,
    default: "",
  },
  availableInspectors: {
    type: Array,
    default: () => [],
  },
  inspectionStatus: {
    type: Array,
    default: () => [],
  },
  checkpointCounts: {
    type: Object,
    default: () => ({}),
  },
  currentBadgeStatus: {
    type: String,
    default: "",
  },
  readinessLevel: {
    type: String,
    default: "Apprentice",
  },
  readinessDot: {
    type: String,
    default: "No Dot",
  },
  checkpointsData: {
    type: Array,
    default: () => [],
  },
  checklistLoading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  checklistCount: {
    type: Number,
    default: 0,
  },
  availableInspectorsCount: {
    type: Number,
    default: 0,
  },
  levelCounter: {
    type: Object,
    default: () => ({
      Apprentice: 0,
      Professional: 0,
      Coach: 0,
      Master: 0,
    }),
  },
  showBreadcrumb: {
    type: Boolean,
    default: false,
  },
  initialTab: {
    type: String,
    default: "Checklist",
  },
  purpose: {
    type: String,
    default: "",
  },
  responsibilityPurpose: {
    type: String,
    default: "",
  },
  inspectionBannerInspectorAccepted: {
    type: Object,
    default: () => null,
  },
  /** Badge data for banner right side. When not provided, derived from checkpointCounts + currentBadgeStatus + readinessLevel. */
  inspectionBannerStatusCounter: {
    type: Object,
    default: undefined,
  },
  /** null = no inspection request; true/false = requested (expedited or not) */
  expedited: {
    default: null,
    validator: (v) => v === null || typeof v === "boolean",
  },
  inspectionBannerIsRequested: {
    type: Boolean,
    default: false,
  },
  inspectionRequestId: {
    type: [Number, String],
    default: null,
  },
  accountId: { type: [Number, String], default: null },
  responsibilityId: { type: [Number, String], default: null },
  employeeId: { type: [Number, String], default: null },
  /** idea-MDP-452 checklist rows use compact `AsEmployeeCheckpointCard` layout. */
  ideaMDP452: {
    type: Boolean,
    default: false,
  },
});

const { proxy } = getCurrentInstance();

// Emits
const emit = defineEmits([
  "appliedFilter",
  "filtersChanged",
  "tab-selected",
  "onBackToTable",
  "paginationChanged",
  "assignment-refreshed",
  "inspection-request-start",
  "inspection-request-error",
  "mdp-cta",
  "complete-microlearning-click",
  "request-more-checkpoints-chatter",
]);

// State
const selectedTab = ref(props.initialTab);
const showRequestInspectionDialog = ref(false);
const showCancelInspectionDialog = ref(false);

// idea-MDP-452 checklist inspection view (fetched from the checklist API)
const checklistItems = ref([]);
const checklistLoaded = ref(false);
const checklistPassFilter = ref("not-yet-passed");
const checklistSearchQuery = ref("");
/** `undefined` = auto-expand lowest incomplete level; `null` = none; else a level name. */
const checklistExpandedLevel = ref(undefined);

const showCheckpointDrawer = ref(false);
const drawerCheckpointItem = ref(null);
const drawerCheckpointDetail = ref(null);
const drawerCheckpointCard = ref(null);
const drawerCheckpointGroup = ref(null);
const drawerCurrentStep = ref(1);
const drawerActionId = ref("");
const drawerQuizAttemptResult = ref(null);
const drawerInspectionVerdict = ref(null);
const drawerIsInspectorOnly = ref(false);
const drawerWidth = ref(0);
/** MDP-625 — quiz-only checkpoints end on a "Checkpoint passed" result step. */
const resultStepEnabled = ref(false);

async function resolveResultStepEnabled() {
  try {
    resultStepEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_625, false)) === true;
  } catch {
    resultStepEnabled.value = false;
  }
}

const drawerCheckpointMerged = computed(() => {
  if (!drawerCheckpointItem.value) return null;
  return drawerCheckpointDetail.value
    ? { ...drawerCheckpointItem.value, ...drawerCheckpointDetail.value }
    : drawerCheckpointItem.value;
});

const drawerLearningEvents = computed(() => {
  const events = drawerCheckpointMerged.value?.learning_events;
  return Array.isArray(events) ? events : [];
});

const drawerSections = computed(() => {
  if (!drawerCheckpointMerged.value) return [];
  if (drawerIsInspectorOnly.value) {
    return buildInspectorOnlyDrawerSections(drawerCheckpointMerged.value);
  }
  return buildCheckpointDrawerSections(drawerCheckpointMerged.value, {
    includeResult: resultStepEnabled.value,
  });
});

const drawerInspectionStatus = computed(
  () =>
    drawerInspectionVerdict.value?.status ||
    resolveInspectionDrawerStatus(drawerCheckpointMerged.value || {})
);

const drawerInspectionDescription = computed(() => drawerInspectionVerdict.value?.reason || "");

const drawerInspectorName = computed(() => drawerInspectionVerdict.value?.inspectorName || "");

const drawerInspectionDate = computed(() => drawerInspectionVerdict.value?.inspectionDate || "");

/** Review (and any already-passed quiz) is view-only — do not retake. */
const drawerReadonly = computed(() => {
  if (drawerActionId.value === "review") return true;
  const quizStatus = String(drawerCheckpointMerged.value?.activity_statuses?.quiz ?? "")
    .trim()
    .toLowerCase();
  return quizStatus === "passed";
});

const drawerLevel = computed(() => {
  if (!drawerCheckpointItem.value) return "Apprentice";
  const fromGroup = drawerCheckpointGroup.value?.level;
  if (fromGroup) return fromGroup;
  return resolveLevelName(drawerCheckpointItem.value);
});

const drawerCriteria = computed(() => {
  if (!drawerCheckpointItem.value) return "";
  return drawerCheckpointCard.value?.title || buildCheckpointTitle(drawerCheckpointItem.value);
});

const drawerResponsibilityCode = computed(
  () => props.responsibility?.code || props.responsibility?.responsibilityCode || ""
);

const drawerEmployee = computed(() => {
  try {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const employee = user.employee || {};
    const firstName = employee.first_name || user.first_name || "";
    const lastName = employee.last_name || user.last_name || "";
    return {
      employeeName: `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      jobTitle: employee.job_title || employee.position || "",
      avatar: employee.avatar || employee.picture || null,
    };
  } catch {
    return {
      employeeName: "",
      firstName: "",
      lastName: "",
      jobTitle: "",
      avatar: null,
    };
  }
});

const checklistLevels = computed(() =>
  buildChecklistInspectionLevels(checklistItems.value, {
    passFilter: checklistPassFilter.value,
    search: checklistSearchQuery.value,
    expandedLevel: checklistExpandedLevel.value,
    levels: EMPLOYEE_CHECKLIST_LEVELS,
  })
);

// Computed
const menuItems = computed(() => [
  {
    label: "Checklist",
    icon: "",
    enabled: true,
    badge: props.checklistCount || props.checkpointsData.length,
  },
  {
    label: "Available Inspectors",
    icon: "",
    enabled: true,
    badge: props.availableInspectorsCount || props.availableInspectors.length,
  },
  {
    label: "Purpose",
    icon: "",
    enabled: true,
  },
]);

const breadcrumbItems = computed(() => [
  {
    text: "Your Responsibilities",
    disabled: false,
  },
  {
    text: "Responsibility Assignment",
    disabled: true,
  },
]);

const hasRequestDialogInspectors = computed(
  () => props.availableInspectors && props.availableInspectors.length > 0
);

/** Explicit banner state, derived from the inspection-request props (see AsEmployeeInspectionBanner). */
const inspectionBannerState = computed(() => {
  const acc = props.inspectionBannerInspectorAccepted;
  const hasAcceptedInspector =
    acc != null && typeof acc === "object" && Object.keys(acc).length > 0;
  if (hasAcceptedInspector) return "accepted";

  const hasInspectors =
    props.availableInspectors && props.availableInspectors.length > 0;
  if (props.inspectionBannerIsRequested) {
    return hasInspectors ? "pending" : "no_inspectors";
  }
  return hasInspectors ? "make_request" : "no_inspectors";
});

const showInspectionRequest = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP176"];
});

// Methods
function handleAppliedFilter(filters) {
  emit("appliedFilter", filters);
}

function handleFiltersChanged(filterData) {
  emit("filtersChanged", filterData);
}

function handleTabSelected(tabLabel) {
  selectedTab.value = tabLabel;
  emit("tab-selected", tabLabel);
  if (tabLabel === "Checklist") loadChecklist();
}
function handleBreadcrumbClick(breadcrumb) {
  emit("onBackToTable");
}

function handlePaginationChanged(paginationData) {
  emit("paginationChanged", paginationData);
}

function handleInspectionRequest() {
  showRequestInspectionDialog.value = true;
}

function resolveAccountId() {
  if (props.accountId != null) return props.accountId;
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

function resolveEmployeeId() {
  if (props.employeeId != null) return props.employeeId;
  try {
    return JSON.parse(localStorage.getItem("user"))?.employee?.id ?? null;
  } catch {
    return null;
  }
}

function handleChecklistToggleLevel({ level, expanded }) {
  checklistExpandedLevel.value = expanded ? level : null;
}

const DRAWER_ACTION_IDS = new Set([
  "start",
  "take-quiz",
  "retake-quiz",
  "review",
  "ask-inspection",
]);

function isBareCloudflareStreamUid(value) {
  return typeof value === "string" && /^[a-f0-9]{32}$/i.test(value.trim());
}

function isCloudflareStreamPlaybackUrl(value) {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  return (
    /cloudflarestream\.com\/[^/?#]+\/iframe/i.test(trimmed) ||
    trimmed.includes("iframe.videodelivery.net")
  );
}

function hasResolvedVideoPlaybackUrl(value) {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed || isBareCloudflareStreamUid(trimmed)) return false;
  return (
    trimmed.toLowerCase().includes("<iframe") ||
    isApiVideoPlaybackUrl(trimmed) ||
    isCloudflareStreamPlaybackUrl(trimmed) ||
    isCloudflareSignedUrl(trimmed) ||
    (!needsSignedVideoUrlResolution(trimmed, isCloudflareSignedUrl) &&
      /^https?:\/\//i.test(trimmed))
  );
}

async function resolveDrawerCheckpointDetail(
  detail,
  { accountId, responsibilityId, checkpointId }
) {
  if (!detail) return detail;

  const hasVideo =
    detail.has_active_video === true ||
    String(detail.active_microlearning_type ?? "").toLowerCase() === "video" ||
    (Array.isArray(detail.activities) &&
      detail.activities.some((activity) => String(activity).toLowerCase() === "video"));

  if (!hasVideo) return detail;

  const microlearningInstructions =
    detail.microlearning_instructions && typeof detail.microlearning_instructions === "object"
      ? detail.microlearning_instructions
      : null;
  const fromInstructions =
    extractPlaybackUrlFromMicrolearningInstructions(microlearningInstructions);
  if (fromInstructions) {
    return {
      ...detail,
      microlearning: fromInstructions,
      active_microlearning: fromInstructions,
    };
  }

  const playbackCandidate = detail.active_microlearning || detail.microlearning || "";
  if (hasResolvedVideoPlaybackUrl(playbackCandidate)) return detail;

  if (accountId == null || responsibilityId == null || checkpointId == null) return detail;

  const hasCloudflareVideoId = Boolean(
    detail.microlearning_cloudflare_video_id ||
    detail.active_microlearning_cloudflare_video_id ||
    detail.activecloudflarevideo_id ||
    detail.active_video_source
  );
  if (!hasCloudflareVideoId) return detail;

  try {
    const { data: signed } = await getCheckpointVideoSignedUrl(
      accountId,
      responsibilityId,
      checkpointId
    );
    const url = signed?.url;
    if (url) {
      return { ...detail, microlearning: url, active_microlearning: url };
    }
  } catch (error) {
    console.error("Error resolving checkpoint video signed URL:", error);
  }

  return detail;
}

async function openCheckpointDrawer({ group, checkpoint, id }) {
  const item = checkpoint?.item || checkpoint;
  if (!item) return;

  drawerWidth.value = Math.round(window.innerWidth * 0.8);
  drawerCheckpointGroup.value = group || null;
  drawerCheckpointCard.value = checkpoint || null;
  drawerCheckpointItem.value = item;
  drawerCheckpointDetail.value = null;
  drawerActionId.value = id || "";
  drawerQuizAttemptResult.value = null;
  drawerInspectionVerdict.value = null;
  drawerIsInspectorOnly.value = isInspectorOnlyCheckpoint(item);

  const accountId = resolveAccountId();
  const responsibilityId = props.responsibilityId;
  const checkpointId = item?.id ?? item?.checkpoint_id;

  const applyDrawerStep = (sections, sourceItem) => {
    drawerCurrentStep.value = findResumeStep(sections, sourceItem || item, id);
  };

  try {
    proxy.$store.commit("isLoading/setAppLoading", true);
    await resolveResultStepEnabled();

    if (accountId != null && responsibilityId != null && checkpointId != null) {
      const { data } = await getEmployeeCheckpointDetail(accountId, responsibilityId, checkpointId);
      drawerCheckpointDetail.value = await resolveDrawerCheckpointDetail(data ?? null, {
        accountId,
        responsibilityId,
        checkpointId,
      });
      if (
        !drawerIsInspectorOnly.value &&
        isInspectorOnlyCheckpoint(drawerCheckpointDetail.value || {})
      ) {
        drawerIsInspectorOnly.value = true;
      }
    }

    applyDrawerStep(drawerSections.value, drawerCheckpointMerged.value);
    showCheckpointDrawer.value = true;
    await loadInspectionVerdictFromChatter(checkpointId);
  } catch (error) {
    console.error("Error loading checkpoint detail:", error);
    applyDrawerStep(
      buildCheckpointDrawerSections(item, { includeResult: resultStepEnabled.value }),
      item
    );
    showCheckpointDrawer.value = true;
    await loadInspectionVerdictFromChatter(checkpointId);
  } finally {
    proxy.$store.commit("isLoading/setAppLoading", false);
  }
}

function resolveDrawerLearningSectionType() {
  const section = drawerSections.value.find(
    (item) => item.type === "article" || item.type === "video"
  );
  return section?.type || null;
}

async function handleChecklistActionClick(payload) {
  const { id } = payload || {};
  if (!DRAWER_ACTION_IDS.has(id)) return;
  await openCheckpointDrawer(payload);
  if (id !== "start") return;
  if (!resolveDrawerLearningSectionType()) return;
  recordCheckpointProgressEvent(LEARNING_SEEN_EVENT);
}

function handleChecklistSecondaryActionClick(payload) {
  openCheckpointDrawer({ ...payload, id: payload?.id || "review" });
}

function closeCheckpointDrawer() {
  showCheckpointDrawer.value = false;
  drawerCheckpointItem.value = null;
  drawerCheckpointDetail.value = null;
  drawerCheckpointCard.value = null;
  drawerCheckpointGroup.value = null;
  drawerCurrentStep.value = 1;
  drawerActionId.value = "";
  drawerQuizAttemptResult.value = null;
  drawerInspectionVerdict.value = null;
  drawerIsInspectorOnly.value = false;
}

async function loadInspectionVerdictFromChatter(checkpointId) {
  const accountId = resolveAccountId();
  if (accountId == null || checkpointId == null) return;

  try {
    const { data } = await getChatterList(accountId, checkpointId, "checkpoint", 1);
    const verdict = extractInspectionVerdictFromChatter(data?.results || []);
    if (verdict) drawerInspectionVerdict.value = verdict;
  } catch (error) {
    console.error("Error loading inspection chatter:", error);
  }
}

const LEARNING_SEEN_EVENT = "learning_seen";

const CHECKPOINT_PROGRESS_EVENTS = {
  article: "article_read",
  video: "video_watched",
  [LEARNING_SEEN_EVENT]: LEARNING_SEEN_EVENT,
};

function resolveDrawerCheckpointId() {
  return (
    drawerCheckpointDetail.value?.id ??
    drawerCheckpointItem.value?.id ??
    drawerCheckpointItem.value?.checkpoint_id ??
    null
  );
}

async function reloadChecklistInBackground() {
  await fetchChecklist({ background: true, preserveExpanded: true });
}

function syncDrawerCheckpointFromList() {
  if (!showCheckpointDrawer.value || !drawerCheckpointItem.value) return;

  const checkpointId = resolveDrawerCheckpointId();
  const refreshedItem = checklistItems.value.find(
    (item) => String(item?.id ?? item?.checkpoint_id) === String(checkpointId)
  );
  if (!refreshedItem) return;

  drawerCheckpointItem.value = refreshedItem;
  if (drawerCheckpointCard.value) {
    drawerCheckpointCard.value = { ...drawerCheckpointCard.value, item: refreshedItem };
  }
}

async function fetchChecklist({ background = false, preserveExpanded = false } = {}) {
  if (!props.ideaMDP452) return;

  const accountId = resolveAccountId();
  const responsibilityId = props.responsibilityId;
  const employeeId = resolveEmployeeId();
  if (accountId == null || responsibilityId == null || employeeId == null) return;

  const expandedLevel = checklistExpandedLevel.value;

  try {
    if (!background) proxy.$loader.show();
    const { data } = await getEmployeeChecklist(
      accountId,
      responsibilityId,
      employeeId,
      resolveChecklistQueryParams("all")
    );
    checklistItems.value = Array.isArray(data) ? data : (data?.results ?? []);
    checklistLoaded.value = true;
    if (preserveExpanded) checklistExpandedLevel.value = expandedLevel;
    syncDrawerCheckpointFromList();
  } catch (error) {
    console.error("Error loading checklist:", error);
  } finally {
    if (!background) proxy.$loader.hide();
  }
}

async function recordCheckpointProgressEvent(sectionType) {
  const event = CHECKPOINT_PROGRESS_EVENTS[sectionType];
  if (!event) return;

  const accountId = resolveAccountId();
  const responsibilityId = props.responsibilityId;
  const employeeId = resolveEmployeeId();
  const checkpointId = resolveDrawerCheckpointId();

  if (accountId == null || responsibilityId == null || employeeId == null || checkpointId == null) {
    return;
  }

  try {
    await postCheckpointInspectionEvent(accountId, responsibilityId, employeeId, checkpointId, {
      event,
    });
    await reloadChecklistInBackground();
  } catch (error) {
    console.error("Error recording checkpoint progress:", error);
  }
}

function handleDrawerNext(nextStep) {
  const previousSection = drawerSections.value[Number(nextStep) - 2];
  const sectionType = previousSection?.type;
  if (sectionType === "article" || sectionType === "video") {
    recordCheckpointProgressEvent(sectionType);
  }
}

async function handleDrawerQuizSubmit(quizPayload) {
  const accountId = resolveAccountId();
  const responsibilityId = props.responsibilityId;
  const employeeId = resolveEmployeeId();
  const checkpointId = resolveDrawerCheckpointId();
  const quizSection = drawerSections.value.find((section) => section.type === "quiz");

  if (
    accountId == null ||
    responsibilityId == null ||
    employeeId == null ||
    checkpointId == null ||
    !Array.isArray(quizPayload)
  ) {
    return;
  }

  const submittedAttempt = buildSubmittedQuizAttempt(quizSection, quizPayload);

  try {
    proxy.$store.commit("isLoading/setAppLoading", true);
    const { data } = await submitCheckpointQuizAttempt(accountId, responsibilityId, checkpointId, {
      employee_id: Number(employeeId),
      submitted_attempt: submittedAttempt,
    });
    drawerQuizAttemptResult.value = data ?? null;
    await reloadChecklistInBackground();
  } catch (error) {
    console.error("Error submitting quiz attempt:", error);
  } finally {
    proxy.$store.commit("isLoading/setAppLoading", false);
  }
}

async function loadChecklist() {
  if (!props.ideaMDP452 || checklistLoaded.value) return;
  await fetchChecklist();
}

async function handleDialogMakeRequest(payload) {
  const accountId = resolveAccountId();
  const responsibilityId = props.responsibilityId;
  const employeeId = props.employeeId;
  const targetLevel = (props.readinessLevel || "apprentice").toLowerCase();
  const expedited = Boolean(payload?.expedited);

  if (accountId != null && responsibilityId != null && employeeId != null) {
    try {
      proxy.$store.commit("isLoading/setAppLoading", true);
      const response = await requestInspection(accountId, responsibilityId, {
        employee: Number(employeeId),
        responsibility: Number(responsibilityId),
        expedited,
        target_level: targetLevel,
      });
      const inspectionResponse = response?.data ?? null;

      let evaluationData = null;
      let summaryByLevelData = null;
      try {
        const evalRes = await getEmployeeEvaluation(accountId, responsibilityId, employeeId, 0, 10);
        evaluationData = evalRes?.data ?? null;
      } catch (e) {
        console.error("Error refetching evaluations after inspection request:", e);
      }
      try {
        const summaryRes = await getEvaluationSummaryByLevel(
          accountId,
          responsibilityId,
          employeeId,
          {}
        );
        summaryByLevelData = summaryRes?.data ?? null;
      } catch (e) {
        console.error("Error refetching summary after inspection request:", e);
      }

      emit("assignment-refreshed", {
        evaluationData,
        summaryByLevelData,
        inspectionResponse,
        expedited,
      });
      showRequestInspectionDialog.value = false;
    } catch (error) {
      console.error("Error requesting inspection:", error);
      emit("inspection-request-error", error);
    } finally {
      proxy.$store.commit("isLoading/setAppLoading", false);
    }
    return;
  }
}

function handleInspectionCancel() {
  if (props.inspectionRequestId == null || props.inspectionRequestId === "") {
    return;
  }
  showCancelInspectionDialog.value = true;
}

/** Chatter table holding the inspection-request lifecycle (Requested/Accepted/…). */
const INSPECTION_REQUEST_CHATTER_TABLE = "evaluation_request";

const showInspectionChatter = ref(false);
const inspectionChatterList = ref([]);
const inspectionChatterTotal = ref(0);
const inspectionChatterPage = ref(1);
const inspectionChatterLoading = ref(false);

const inspectionChatterRecord = computed(() => ({
  id: props.inspectionRequestId ?? null,
}));

async function handleInspectionChatter() {
  const requestId = props.inspectionRequestId;
  if (requestId == null || requestId === "") return;
  if (inspectionChatterLoading.value) return;

  inspectionChatterList.value = [];
  inspectionChatterTotal.value = 0;
  inspectionChatterPage.value = 1;
  await loadInspectionChatterPage(1);
  showInspectionChatter.value = true;
}

async function loadInspectionChatterPage(page = 1, append = false) {
  const accountId = resolveAccountId();
  const requestId = props.inspectionRequestId;
  if (accountId == null || requestId == null || inspectionChatterLoading.value) return;

  inspectionChatterLoading.value = true;
  try {
    const { data } = await getChatterList(
      accountId,
      requestId,
      INSPECTION_REQUEST_CHATTER_TABLE,
      page
    );
    const results = transformChatterData(data?.results || []);
    inspectionChatterTotal.value = data?.count || 0;
    inspectionChatterList.value = append
      ? [...inspectionChatterList.value, ...results]
      : results;
    inspectionChatterPage.value = page;
  } catch (error) {
    console.error("Error loading inspection request chatter:", error);
  } finally {
    inspectionChatterLoading.value = false;
  }
}

function loadMoreInspectionChatter() {
  if (inspectionChatterTotal.value <= inspectionChatterList.value.length) return;
  loadInspectionChatterPage(inspectionChatterPage.value + 1, true);
}

function closeInspectionChatter() {
  showInspectionChatter.value = false;
}

async function handleCancelInspectionConfirm() {
  const accountId = resolveAccountId();
  const responsibilityId = props.responsibilityId;
  const evaluationId = props.inspectionRequestId;
  const employeeId = props.employeeId;

  if (accountId == null || responsibilityId == null || evaluationId == null) {
    console.error("Cancel inspection: missing accountId, responsibilityId, or evaluation id");
    return;
  }

  try {
    proxy.$store.commit("isLoading/setAppLoading", true);
    await cancelInspectionRequest(accountId, responsibilityId, evaluationId);

    let evaluationData = null;
    let summaryByLevelData = null;
    if (employeeId != null) {
      try {
        const evalRes = await getEmployeeEvaluation(accountId, responsibilityId, employeeId, 0, 10);
        evaluationData = evalRes?.data ?? null;
      } catch (e) {
        console.error("Error refetching evaluations after cancel:", e);
      }
      try {
        const summaryRes = await getEvaluationSummaryByLevel(
          accountId,
          responsibilityId,
          employeeId,
          {}
        );
        summaryByLevelData = summaryRes?.data ?? null;
      } catch (e) {
        console.error("Error refetching summary after cancel:", e);
      }
    }

    emit("assignment-refreshed", {
      evaluationData,
      summaryByLevelData,
      inspectionCancelled: true,
    });
    showCancelInspectionDialog.value = false;
  } catch (error) {
    console.error("Error cancelling inspection request:", error);
    emit("inspection-request-error", error);
  } finally {
    proxy.$store.commit("isLoading/setAppLoading", false);
  }
}

// Watch for changes to initialTab prop
watch(
  () => props.initialTab,
  (newTab) => {
    if (newTab) {
      selectedTab.value = newTab;
    }
  }
);

watch(checklistPassFilter, () => {
  checklistExpandedLevel.value = undefined;
});

onMounted(() => {
  if (selectedTab.value === "Checklist") loadChecklist();
});

defineExpose({
  checklistPassFilter,
  checklistExpandedLevel,
  fetchChecklist,
  handleInspectionChatter,
  handleChecklistActionClick,
  handleDrawerNext,
});
</script>

<style scoped>
.your-responsibility-checklist-tab {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
}

.tab-content {
  min-height: 200px;
}

.coming-soon {
  text-align: center;
  color: #667085;
  font-size: 16px;
  font-weight: 500;
  padding: 60px 20px;
}
.assignment-header {
  padding: 0 16px;
}
.breadcrumb-container {
  padding: 0 16px 15px 16px;
}
.inspection-banner-wrap {
  margin: 16px;
}
.responsibility-purpose-tab {
  padding: 0px 24px 24px 24px;
  background-color: #ffffff;
}
::v-deep .inspection-banner {
  width: auto !important;
}
</style>
